import React, {useContext, useEffect, useState} from "react";
import Dialog from "../../components/Dialog";
import {EditRow, EditTable} from "../../components/Fields";
import Number from "../../components/Number";
import NumberNullable from "../../components/NumberNullable";
// Unit conversion deactivated - restore this import to re-enable the weight-format picker:
// import {SelectString} from "../../components/SelectString";
import SelectNumberNullable from "../../components/SelectNumberNullable";
import FarmController from "../../generated/controllers/farmController";
import BlockCropController from "../../generated/controllers/blockCropController";
import BlockRowDisplay from "../../generated/interfaces/blockRowDisplay";
import CropBrakePointDisplay from "../../generated/interfaces/cropBrakePointDisplay";
import Cultivar from "../../generated/interfaces/cultivar";
import YieldWeightUnit from "../../generated/interfaces/yieldWeightUnit";
import AppContext from "../appContext";
import Failed from "../../components/Failed";
import Success from "../../components/Success";
import Warning, {useWarningState} from "../../components/Warning";

// Weight-unit metadata, shared with the block table so labels stay in sync.
export const unitOptions: Record<string, string> = {
    'KG': YieldWeightUnit.Kg,
    'Ton': YieldWeightUnit.Ton,
    'Ton/ha': YieldWeightUnit.TonPerHa,
    'Totale Kartonne': YieldWeightUnit.TotaleKartonne,
    'Kartonne / ha': YieldWeightUnit.KartonnePerHa,
    'Total Bins': YieldWeightUnit.TotaleBins,
    'Bins / ha': YieldWeightUnit.BinsPerHa
}

export const unitText: Record<string, string> = {
    [YieldWeightUnit.Kg]: 'KG',
    [YieldWeightUnit.Ton]: 'Ton',
    [YieldWeightUnit.TonPerHa]: 'Ton/ha',
    [YieldWeightUnit.TotaleKartonne]: 'Totale Kartonne',
    [YieldWeightUnit.KartonnePerHa]: 'Kartonne / ha',
    [YieldWeightUnit.TotaleBins]: 'Total Bins',
    [YieldWeightUnit.BinsPerHa]: 'Bins / ha'
}

function isBinUnit(unit: YieldWeightUnit): boolean {
    return unit === YieldWeightUnit.TotaleBins || unit === YieldWeightUnit.BinsPerHa
}

// Cartons and bins are counted containers that need a per-unit weight to reach KG.
export function needsUnitWeight(unit: YieldWeightUnit): boolean {
    return unit === YieldWeightUnit.TotaleKartonne || unit === YieldWeightUnit.KartonnePerHa || isBinUnit(unit)
}

function unitWeightLabel(unit: YieldWeightUnit): string {
    return isBinUnit(unit) ? 'Bin weight (KG)' : 'Carton weight (KG)'
}

// Crop dropdown label: crop name with its brake-point cap shown for reference, e.g. "Apples (3.00 T/ha)".
function cropLabel(c: CropBrakePointDisplay): string {
    return c.maxTHa != null ? `${c.cropName} (${c.maxTHa.toFixed(2)} T/ha)` : c.cropName
}

// Farm > View dialog: edit one block's season weight and its crop/cultivar in a single popup. The
// weight is optional - leaving the value at 0 saves only the crop/cultivar (see FarmController.SaveBlock).
const BlockEditDialog: React.FC<{
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    block: BlockRowDisplay;
    seasonId: number | null;
    onSaved: () => void;
}> = (props) => {
    const context = useContext(AppContext)
    const [unit, setUnit] = useState<YieldWeightUnit>(YieldWeightUnit.Kg)
    const [value, setValue] = useState(0)
    const [cartonWeightKg, setCartonWeightKg] = useState<number | null>(null)
    const [cropId, setCropId] = useState<number | null>(null)
    const [cultivarId, setCultivarId] = useState<number | null>(null)
    const [crops, setCrops] = useState<CropBrakePointDisplay[]>([])
    const [cultivars, setCultivars] = useState<Cultivar[]>([])
    const saveWarning = useWarningState<null>(null)

    // Re-seed the form whenever a different block is opened.
    useEffect(() => {
        if (!props.show) return
        setUnit(props.block.unit ?? YieldWeightUnit.Kg)
        setValue(props.block.value ?? 0)
        setCartonWeightKg(props.block.cartonWeightKg)
        setCropId(props.block.cropId)
        setCultivarId(props.block.cultivarId)
        if (props.block.cropId != null) loadCultivars(props.block.cropId)
        else setCultivars([])
    }, [props.show, props.block])

    useEffect(() => {
        BlockCropController.crops().then(setCrops)
    }, [])

    function loadCultivars(crop: number) {
        BlockCropController.cultivarsForCrop({id: crop}).then(setCultivars)
    }

    function changeCrop(next: number | null) {
        setCropId(next)
        setCultivarId(null)
        if (next != null) loadCultivars(next)
        else setCultivars([])
    }

    function save() {
        if (value > 0 && needsUnitWeight(unit) && (cartonWeightKg == null || cartonWeightKg <= 0)) {
            context.showSnack(<Failed title='Failed' text='A per unit weight in KG is required for Carton and Bin formats.'/>)
            return
        }

        // Saving clears the block's approval + ready state server-side, so confirm before persisting
        // when the block is currently approved & ready for processing.
        if (props.block.readyForProcessing) {
            saveWarning.show(
                'Re-approval required',
                'This block is approved and ready for processing. Saving these changes will clear its approval and remove it from processing. You will need to re-approve and re-mark it ready afterwards.',
                null
            )
            return
        }

        persist()
    }

    function persist() {
        FarmController.saveBlock({
            landId: props.block.landId,
            seasonId: props.seasonId,
            unit,
            value,
            cartonWeightKg: needsUnitWeight(unit) ? cartonWeightKg : null,
            cropId,
            cultivarId
        }).then(() => {
            context.showSnack(<Success title='Saved' text={`${props.block.name} updated.`}/>)
            props.onSaved()
            props.setShow(false)
        }).catch(() => {
            context.showSnack(<Failed title='Failed' text='Could not save the block.'/>)
        })
    }

    return (
        <>
        <Dialog title={`Edit: ${props.block.name}`}
                key="blockEdit"
                show={props.show}
                setShow={props.setShow}
                body={
                    <div className="m-2 min-w-[320px]">
                        <EditTable discard={() => props.setShow(false)} save={save} saveWord='Save'>
                            {EditRow('Block size', `${props.block.ha.toFixed(2)} ha`)}

                            {/* Unit conversion deactivated - restore to re-enable the weight-format picker:
                            {EditRow('Weight format', <SelectString
                                value={unit}
                                onChange={u => setUnit(u as YieldWeightUnit)}
                                options={unitOptions}
                            />)}
                            */}

                            {EditRow('Weight (KG)', <Number value={value} change={setValue}/>)}
                            {needsUnitWeight(unit)
                                ? EditRow(unitWeightLabel(unit), <NumberNullable value={cartonWeightKg} change={setCartonWeightKg}/>)
                                : null}
                            {EditRow('Crop (Brakepoint)', <SelectNumberNullable<CropBrakePointDisplay>
                                value={cropId}
                                onChange={changeCrop}
                                options={crops}
                                textFunc={cropLabel}
                                valueFunc={c => c.cropId}
                                nullableText='No crop'
                            />)}
                            {EditRow('Cultivar', cropId == null
                                ? <span className="text-gray-400">Select a crop first</span>
                                : <SelectNumberNullable<Cultivar>
                                    value={cultivarId}
                                    onChange={setCultivarId}
                                    options={cultivars}
                                    textFunc={c => c.name}
                                    valueFunc={c => c.id}
                                    nullableText='No cultivar'
                                />)}
                        </EditTable>
                    </div>
                }
        />
        <Warning state={saveWarning} onYes={() => persist()} yesLabel="Save anyway" noLabel="Cancel"/>
        </>
    )
}

export default BlockEditDialog
